import classnames from 'classnames';
import * as React from 'react';

/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { ProductPrice, ProjectionDomain } from '@msdyn365-commerce/retail-proxy';
import { getActivePricesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { GetLoyaltyCardInput, getLoyaltyAction } from '../../actions/get-loyalty-card.override.action';
export interface IPriceComponentProps extends IComponentProps<{ price: ProductPrice }> {
    className?: string;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    currencyCode?: string;
    quantity?: number;
    shouldNotShowWasText?: boolean;
}
export interface IPriceComponent extends IComponent<IPriceComponentProps> {}

const PriceComponentActions = {};

const Price: React.FC<IPriceComponentProps> = (props: IPriceComponentProps) => {
    const [activePrices, setActivePrices] = React.useState<ProductPrice[]>([]);
    const [activeTier, setActiveTier] = React.useState<number>();
    // CustomerContextualPrice could be 0
    if (props.data.price.CustomerContextualPrice === undefined) {
        return null;
    }

    React.useEffect(() => {
        getProductPrices();
        getTierId();
    }, []);

    const getProductPrices = async () => {
        try {
            const priceAffiliations = [
                {
                    AffiliationId: props.context.app.config.AffiliationId,
                    LoyaltyTierId: props.context.app.config.LoyaltyTierId,
                    AffiliationTypeValue: props.context.app.config.AffiliationTypeValue,
                    ReasonCodeLines: [],
                    CustomerId: '',
                    ExtensionProperties: []
                }
            ];
            const productRecIds: any = [];
            productRecIds.push(props.data.price?.ProductId);
            const projectDomain: ProjectionDomain = {
                ChannelId: props.context.request.apiSettings.channelId,
                CatalogId: props.context.request.apiSettings.catalogId
            };

            await getActivePricesAsync(
                { callerContext: props.context.actionContext, queryResultSettings: {} },
                projectDomain,
                productRecIds,
                new Date(),
                null,
                priceAffiliations,
                true
            )
                .then((activePricesResponse: ProductPrice[]) => {
                    if (activePricesResponse) {
                        setActivePrices(activePricesResponse);
                    }
                })
                .catch((error: Error) => {
                    props.context.actionContext.trace(error.message);
                    props.context.actionContext.telemetry.exception(error);
                    props.context.actionContext.telemetry.debug(
                        '[get Affiliation based price on search result container ]Error executing action'
                    );
                    throw new Error('[get Affiliation based price on search result container]Error executing action');
                });
        } catch (err) {
            console.error(err, 'ERROR');
        }
    };
    const getTierId = async () => {
        if (props.context.actionContext.requestContext.user.isAuthenticated) {
            const testt = new GetLoyaltyCardInput(
                props.context.actionContext.requestContext.apiSettings,
                props.context.actionContext.requestContext.user.customerAccountNumber
            );
            const loyaltyCheck = await getLoyaltyAction(testt, props.context.actionContext);
            if (loyaltyCheck && loyaltyCheck?.LoyaltyGroups!?.length > 0) {
                const highestActiveLoyaltyCardTier = loyaltyCheck!.LoyaltyGroups![0]?.HighestActiveLoyaltyCardTier;
                const loyaltyCardTiers = loyaltyCheck!.LoyaltyGroups![0]?.LoyaltyCardTiers;

                const loyaltyCardTier = loyaltyCardTiers?.find(tier => tier.RecordId === highestActiveLoyaltyCardTier);
                setActiveTier(loyaltyCardTier?.LoyaltyTierRecordId);
            }
        }
    };
    if (props.context.actionContext.requestContext.urlTokens.pageType === 'Product') {
        if (activePrices.length === 0) {
            return <></>;
        }

        const isLoyaltyUser = activeTier === props.context.app.config.LoyaltyTierId;
        const isMembershipDiscount = activePrices?.[0]?.DiscountLines?.find(
            x => x.OfferId === props.context.app.config.membershipDiscountId
        );
        const checkClubPlusDiscounts = activePrices?.[0]?.DiscountLines?.find(x => x.OfferId === props.context.app.config.discountId);
        const pricesCheck = activePrices && isLoyaltyUser ? { ...props, data: { ...props.data, price: activePrices[0] } } : props;

        if (isMembershipDiscount) {
            return (
                <span className={classnames('msc-price', pricesCheck.className)}>
                    {showStrikethroughPricing(pricesCheck)
                        ? renderCurrentPriceWithOriginalPrice(pricesCheck, checkClubPlusDiscounts?.OfferId, isLoyaltyUser)
                        : renderCurrentPrice(pricesCheck)}
                </span>
            );
        }

        return (
            <span className={classnames('msc-price', props.className)}>
                {showStrikethroughPricing(props)
                    ? renderCurrentPriceWithOriginalPrice(props, checkClubPlusDiscounts?.OfferId, isLoyaltyUser)
                    : renderCurrentPrice(props)}
            </span>
        );
    }
    return (
        <span className={classnames('msc-price', props.className)}>
            {showStrikethroughPricing(props) ? renderCurrentPriceWithOriginalPrice(props) : renderCurrentPrice(props)}
        </span>
    );
};

const showStrikethroughPricing = (props: IPriceComponentProps) => {
    const originalPrice = getOriginalPrice(props);

    if (originalPrice && props.data.price.CustomerContextualPrice) {
        return originalPrice > props.data.price.CustomerContextualPrice;
    }

    return false;
};

const getOriginalPrice = (props: IPriceComponentProps) => {
    return Math.max(props.data.price.BasePrice || 0, props.data.price.TradeAgreementPrice || 0, props.data.price.AdjustedPrice || 0);
};

const renderCurrentPrice = (props: IPriceComponentProps, clubPlusCheck?: string, isLoyaltyUser?: boolean): JSX.Element | undefined => {
    const initialPrice = formatCurrency(props, props.data.price.CustomerContextualPrice);
    return (
        <span className={`msc-price__actual ${clubPlusCheck && isLoyaltyUser ? 'msc-price__strikethrough' : ''}`} itemProp='price'>
            {initialPrice}
        </span>
    );
};

const formatCurrency = (props: IPriceComponentProps, price: number | undefined): string | undefined => {
    if (price === undefined) {
        return undefined;
    }

    if (price === 0 && props.freePriceText) {
        return props.freePriceText;
    }

    if (props.quantity) {
        price = price * props.quantity;
    }

    let formattedPrice = props.context.cultureFormatter.formatCurrency(price, props.currencyCode);

    if (!formattedPrice.includes('.')) {
        formattedPrice += '.00';
    } else {
        const [integerPart, decimalPart] = formattedPrice.split('.');
        if (decimalPart.length === 1) {
            formattedPrice = `${integerPart}.${decimalPart}0`;
        }
    }

    return formattedPrice;
};

const renderCurrentPriceWithOriginalPrice = (
    props: IPriceComponentProps,
    clubPlusCheck?: string,
    isLoyaltyUser?: boolean
): JSX.Element | null => {
    const originalPrice = getOriginalPrice(props);
    const initialPrice = formatCurrency(props, originalPrice);

    return (
        <>
            <span className='sr-only'>
                {' '}
                {props.originalPriceText} {initialPrice} {props.currentPriceText} {renderCurrentPrice(props)}
            </span>
            {/* VSI Customization - Start - 23/10/2020 */}
            {/* Displayed discounted price on the left, Actual price on the right */}
            <span className='msc-price__strikethrough' aria-hidden='true'>
                {!props.shouldNotShowWasText ? (
                    <>
                        Was <span>{initialPrice}</span>
                    </>
                ) : (
                    `${initialPrice}`
                )}
            </span>
            {clubPlusCheck && isLoyaltyUser ? (
                <span className='msc-price__salePrice' aria-hidden='true'>
                    {renderCurrentPrice(props, clubPlusCheck, isLoyaltyUser)}
                </span>
            ) : (
                <span className='msc-price__salePrice' aria-hidden='true'>
                    {renderCurrentPrice(props)}
                </span>
            )}

            {/* VSI - Customization - End */}
            {props.savingsText && <span className='msc-price__savings'>{props.savingsText}</span>}
        </>
    );
};

// @ts-ignore
export const PriceComponent: React.FunctionComponent<IPriceComponentProps> = msdyn365Commerce.createComponentOverride<IPriceComponent>(
    'Price',
    { component: Price, ...PriceComponentActions }
);

export default PriceComponent;
